import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useClients } from '../contexts/ClientContext';
import {
  parseClientIdParam,
  resolveScopedClientId,
} from '../utils/lastSelectedClientStorage';

/**
 * Resolves the active client for scoped pages: ?client_id= deep link,
 * then lastSelectedClientId from ClientContext, then first client.
 */
export function useScopedClientSelection({ syncUrlOnSelect = true } = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    clients,
    loaded,
    lastSelectedClientId,
    setLastSelectedClientId,
  } = useClients();

  const urlClientId = parseClientIdParam(searchParams.get('client_id'));

  const clientId = useMemo(
    () =>
      resolveScopedClientId({
        clients,
        urlClientId,
        persistedClientId: lastSelectedClientId,
      }),
    [clients, urlClientId, lastSelectedClientId],
  );

  useEffect(() => {
    if (!loaded || urlClientId == null) return;
    if (clients.some((c) => c.id === urlClientId)) {
      setLastSelectedClientId(urlClientId);
    }
  }, [loaded, urlClientId, clients, setLastSelectedClientId]);

  const selectedClient = useMemo(
    () => clients.find((c) => c.id === clientId) ?? null,
    [clients, clientId],
  );

  const selectClient = useCallback(
    (nextClientOrId, { syncUrl = syncUrlOnSelect } = {}) => {
      const rawId =
        typeof nextClientOrId === 'object'
          ? nextClientOrId?.id
          : nextClientOrId;
      const parsed = parseClientIdParam(rawId);
      if (parsed == null) return;
      setLastSelectedClientId(parsed);
      if (syncUrl) {
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.set('client_id', String(parsed));
            return next;
          },
          { replace: true },
        );
      }
    },
    [setLastSelectedClientId, setSearchParams, syncUrlOnSelect],
  );

  return {
    clientId,
    selectedClient,
    selectClient,
    loaded,
    urlClientId,
  };
}
